import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { ListMyServersResponse, ServerAction, ServerView } from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { linkTo, navigate } from '../router';
import { SleepButton } from '../ui/ActionButtons';
import { Campfire } from '../ui/Campfire';
import { CopyAddress } from '../ui/CopyAddress';
import { BackupsTab } from './Backups';
import { FriendsTab } from './Friends';
import { SettingsTab } from './Settings';
import { UsageTab } from './Usage';
import { useRefresh, useWakeSleep } from './serverHooks';
import { campfireFor, cardKind, detailRows, errorText, howToJoin, joinAddress, joinNote, may, metaText, pollMs, statusText, statusTone } from './serverWords';

/** The server page's tabs, each its own address; a tab with an `action` only for those allowed it (hidden, never disabled). */
export type ServerTab = 'overview' | 'friends' | 'backups' | 'usage' | 'settings';
const TABS: { tab: ServerTab; label: string; path: string; action?: ServerAction }[] = [
  { tab: 'overview', label: 'Overview', path: '' },
  { tab: 'friends', label: 'Friends', path: '/friends' },
  { tab: 'backups', label: 'Backups', path: '/backups', action: 'backups' },
  { tab: 'usage', label: 'Usage', path: '/usage' },
  { tab: 'settings', label: 'Settings', path: '/settings', action: 'settings' },
];

/**
 * /app/servers/{id}: one server. Its campfire, status and Wake or Sleep, then its tabs: Overview
 * (how to join, details) and Friends (who plays here, the invite link). Refreshes like the list.
 * A server that doesn't exist and one you're not on look the same: not found. Its address names it by
 * its handle (D4), found in your own list; an ID (older links, admins' links, just after create)
 * still opens it, and the address then shows the handle.
 */
export function ServerPage({ api, serverId, meId, discordConnected = false, tab = 'overview' }: { api: Api; serverId: string; meId: string; discordConnected?: boolean; tab?: ServerTab }) {
  const [server, setServer] = useState<ServerView>();
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState('');

  // Any request about this server answering 404 means it's gone for this person (destroyed, or
  // they were removed while the page was open): the not-found card, not an error bar.
  const gone = useCallback(() => setMissing(true), []);
  const refused = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) gone();
      else setError(errorText(err));
    },
    [gone],
  );

  // Which server the address names: a handle (always with a hyphen; IDs never have one) is looked
  // up once in your own list, then kept.
  const known = useRef<{ name: string; id: string } | undefined>(undefined);
  // Loads follow the server the address names, not the address itself: swapping an ID for its
  // handle, or moving between tabs, is the same server and asks the API nothing.
  const [named, setNamed] = useState(serverId);
  const aliases = known.current ? [known.current.name, known.current.id] : [];
  if (serverId !== named && !(aliases.includes(serverId) && aliases.includes(named))) setNamed(serverId);
  const address = useRef({ serverId, tab });
  address.current = { serverId, tab };
  const load = useCallback(async () => {
    try {
      const k = known.current;
      let id = k && (named === k.name || named === k.id) ? k.id : named.includes('-') ? undefined : named;
      if (!id) {
        id = (await api.get<ListMyServersResponse>('/v1/servers')).servers.find((s) => s.handle === named)?.serverId;
        if (!id) return gone();
      }
      const view = await api.get<ServerView>(`/v1/servers/${encodeURIComponent(id)}`);
      known.current = { name: view.handle, id: view.serverId };
      setServer(view);
      setError('');
      const { serverId: shown, tab: open } = address.current;
      if (shown !== view.handle) {
        navigate(`/app/servers/${view.handle}${TABS.find((t) => t.tab === open)?.path ?? ''}`, { replace: true });
      }
    } catch (err) {
      refused(err);
    }
  }, [api, named, gone, refused]);
  useRefresh(load, pollMs(server ? [server] : []));
  const { askWake, askSleep, dialog } = useWakeSleep(api, load, refused);

  return (
    <>
      <ServerPageView
        server={server}
        missing={missing}
        error={error}
        now={new Date()}
        tab={tab}
        friends={server && <FriendsTab api={api} server={server} meId={meId} onGone={gone} />}
        backups={server && <BackupsTab api={api} server={server} onChanged={load} onGone={gone} />}
        usage={server && <UsageTab api={api} server={server} onGone={gone} />}
        settings={server && <SettingsTab key={server.serverId} api={api} server={server} discordConnected={discordConnected} onChanged={load} onGone={gone} />}
        onWake={askWake}
        onSleep={askSleep}
      />
      {dialog}
    </>
  );
}

/** What the page shows, given what's loaded (no fetching: easy to test). */
export function ServerPageView(props: {
  server: ServerView | undefined;
  missing: boolean;
  error: string;
  now: Date;
  tab?: ServerTab;
  /** The Friends, Backups, Usage and Settings tabs' content (each loads its own data). */
  friends?: ReactNode;
  backups?: ReactNode;
  usage?: ReactNode;
  settings?: ReactNode;
  onWake: (server: ServerView) => void;
  onSleep: (server: ServerView) => void;
}) {
  const { server, missing, error, now, tab = 'overview', friends, backups, usage, settings, onWake, onSleep } = props;
  // A tab someone may not use isn't shown, and its address shows the Overview instead.
  const tabs = server ? TABS.filter((t) => !t.action || may(server, t.action)) : [];
  const shown = tabs.some((t) => t.tab === tab) ? tab : 'overview';
  const address = server && joinAddress(server);
  return (
    <>
      <a className="back" {...linkTo('/app')}>← Servers</a>
      {error && <p className="notice" role="alert">{error}</p>}
      {missing ? (
        <div className="card">
          <h1>Server not found</h1>
          <p className="quiet">It may have been removed, or you're not on it. If a friend invited you, ask them for a new link.</p>
        </div>
      ) : !server ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : (
        <>
          <header className="server-hero">
            <span className="server-hero-fire"><Campfire state={campfireFor(server)} height={60} /></span>
            <div className="server-hero-text">
              <h1 className="page-title">{server.name}</h1>
              <p className="quiet">{metaText(server)}</p>
              <p className={`server-status server-status-${statusTone(server)}`} aria-live="polite">{statusText(server, now)}</p>
            </div>
            <HeroAction server={server} onWake={() => onWake(server)} onSleep={() => onSleep(server)} />
          </header>
          <nav className="tabs" aria-label={`${server.name}: sections`}>
            {tabs.map((t) => (
              <a key={t.tab} className="tab" aria-current={t.tab === shown ? 'page' : undefined} {...linkTo(`/app/servers/${server.handle}${t.path}`)}>
                {t.label}
              </a>
            ))}
          </nav>
          {shown === 'friends' ? (
            friends
          ) : shown === 'usage' ? (
            usage
          ) : shown === 'settings' ? (
            settings
          ) : shown === 'backups' ? (
            backups
          ) : (
            <div className="server-panels">
              <section className="panel panel-join">
                <h2>Join</h2>
                {address ? (
                  <>
                    <div className="server-row"><CopyAddress value={address} /></div>
                    <p className="quiet">{cardKind(server) === 'changing' ? joinNote(server) : howToJoin(server)}</p>
                  </>
                ) : (
                  <p className="quiet">{joinNote(server)}</p>
                )}
              </section>
              <section className="panel">
                <h2>Details</h2>
                <dl className="details">
                  {detailRows(server, now).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Sleep while it runs, Wake while it's asleep, nothing while it changes: for those allowed to. */
function HeroAction({ server, onWake, onSleep }: { server: ServerView; onWake: () => void; onSleep: () => void }) {
  const kind = cardKind(server);
  if (kind === 'running' && may(server, 'stop')) return <SleepButton serverName={server.name} onClick={onSleep} />;
  if (kind === 'asleep' && server.status !== 'DESTROYED' && may(server, 'start')) {
    return (
      <button className="btn btn-primary btn-tall" type="button" aria-label={`Wake ${server.name}`} onClick={onWake}>
        <Campfire state="lit" height={20} still />
        Wake
      </button>
    );
  }
  return null;
}
